'use client'

import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, CheckCircle2, Clock, XCircle } from 'lucide-react'
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Button } from 'components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Checkbox } from 'components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from 'components/ui/dialog'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Progress } from 'components/ui/progress'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Separator } from 'components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs'
import { Textarea } from 'components/ui/textarea'
import { toast } from 'sonner'
import { auth, db } from '../../../../firebase'
import { useUser } from 'components/context/user-context'
import { getUserId, isUserAssociatedWithProposal } from 'lib/access'

const PROPOSAL_STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  review: 'Under Review',
  revisions: 'Revisions',
  approved: 'Approved',
  won: 'Won',
  lost: 'Lost',
}

const STATUS_BADGE_STYLES = {
  draft: 'bg-muted text-muted-foreground border-border',
  submitted: 'bg-primary/15 text-primary border-primary/30',
  review: 'bg-warning/15 text-warning border-warning/30',
  revisions: 'bg-secondary/50 text-secondary-foreground border-secondary',
  approved: 'bg-success/15 text-success border-success/30',
  won: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/30',
  lost: 'bg-destructive/15 text-destructive border-destructive/30',
}

function formatCurrency(value, currency = 'KES') {
  const amount = Number.isFinite(value) ? value : 0
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount)
}

function formatDate(value) {
  if (!value) return '—'
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function StatusBadge({ status }) {
  const label = PROPOSAL_STATUS_LABELS[status] || status || 'Unknown'
  const style = STATUS_BADGE_STYLES[status] || 'bg-muted text-muted-foreground border-border'
  return (
    <Badge className={`text-[10px] border ${style}`}>
      {label}
    </Badge>
  )
}

const normalizeDate = (value) => {
  if (!value) return value
  if (value?.toDate) return value.toDate()
  return value
}

export default function ProposalDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user, accessLevel, canEdit } = useUser()
  const [proposal, setProposal] = React.useState(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const [statusValue, setStatusValue] = React.useState('draft')
  const [statusComment, setStatusComment] = React.useState('')
  const [savingStatus, setSavingStatus] = React.useState(false)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [editableBudgetItems, setEditableBudgetItems] = React.useState([])
  const [savingBudget, setSavingBudget] = React.useState(false)
  const [users, setUsers] = React.useState([])
  const [availableRoles, setAvailableRoles] = React.useState([])
  const [selectedMemberId, setSelectedMemberId] = React.useState('')
  const [memberRole, setMemberRole] = React.useState('')
  const [newMemberName, setNewMemberName] = React.useState('')
  const [newMemberEmail, setNewMemberEmail] = React.useState('')
  const [isAddingMember, setIsAddingMember] = React.useState(false)
  const [reviewForm, setReviewForm] = React.useState({
    relevance: 0,
    methodology: 0,
    budget: 0,
    impact: 0,
    comments: '',
    conflictDeclared: false,
  })
  const [savingReview, setSavingReview] = React.useState(false)

  React.useEffect(() => {
    let isMounted = true

    async function loadProposal() {
      if (!id) return
      setIsLoading(true)
      try {
        const snapshot = await getDoc(doc(db, 'proposals', id))
        if (!snapshot.exists()) {
          toast.error('Proposal not found')
          navigate('/dashboard/proposals')
          return
        }
        if (!isMounted) return
        const data = snapshot.data() || {}
        const normalized = {
          id: snapshot.id,
          ...data,
          statusHistory: Array.isArray(data.statusHistory) ? data.statusHistory : [],
          budgetItems: Array.isArray(data.budgetItems) ? data.budgetItems : [],
          reviewScores: Array.isArray(data.reviewScores) ? data.reviewScores : [],
          members: Array.isArray(data.members) ? data.members : [],
        }
        const limited = accessLevel === 'limited'
        const currentUserId = getUserId(user)
        if (limited && !isUserAssociatedWithProposal(normalized, currentUserId)) {
          toast.error('You do not have access to this proposal.')
          navigate('/dashboard/proposals')
          return
        }
        setProposal(normalized)
        setStatusValue(normalized.status || 'draft')
        setEditableBudgetItems(
          normalized.budgetItems.map((item, index) => ({
            id: item.id || `line-${index}`,
            category: item.category || 'Other',
            description: item.description || '',
            quantity: Number(item.quantity) || 0,
            unitCost: Number(item.unitCost) || 0,
            total: Number(item.total) || Number(item.quantity || 0) * Number(item.unitCost || 0),
          })),
        )
      } catch (error) {
        toast.error('Failed to load proposal', {
          description: error?.message || 'Please try again.',
        })
      } finally {
        if (isMounted) {
          setIsLoading(false)
        }
      }
    }

    loadProposal()

    return () => {
      isMounted = false
    }
  }, [id, navigate, accessLevel, user?.userId, user?.uid])

  React.useEffect(() => {
    let isMounted = true

    async function loadUsers() {
      try {
        const snapshot = await getDocs(collection(db, 'users'))
        if (!isMounted) return
        const records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        setUsers(records)
      } catch {
        setUsers([])
      }
    }

    async function loadRoles() {
      try {
        const snapshot = await getDocs(collection(db, 'userRoles'))
        if (!isMounted) return
        const roles = snapshot.docs
          .map((docItem) => docItem.data()?.userRole)
          .filter(Boolean)
        setAvailableRoles(Array.from(new Set(roles)))
      } catch {
        setAvailableRoles([])
      }
    }

    loadUsers()
    loadRoles()

    return () => {
      isMounted = false
    }
  }, [])

  const stageKeys = Object.keys(PROPOSAL_STATUS_LABELS)
  const currentIdx = proposal ? stageKeys.indexOf(proposal.status || 'draft') : 0
  const isEditable = proposal?.status === 'draft' && canEdit
  const avgScore =
    proposal && proposal.reviewScores.length > 0
      ? Math.round(
          proposal.reviewScores.reduce((sum, review) => sum + review.total, 0) /
            proposal.reviewScores.length,
        )
      : null

  const reviewTotal = React.useMemo(() => {
    const { relevance, methodology, budget, impact } = reviewForm
    return Number(relevance) + Number(methodology) + Number(budget) + Number(impact)
  }, [reviewForm])

  const handleStatusUpdate = async () => {
    if (!proposal) return
    if (statusValue === proposal.status) {
      toast.error('Please select a new status before updating.')
      return
    }
    if (savingStatus) return
    setSavingStatus(true)
    const currentUser = auth.currentUser
    const statusEvent = {
      status: statusValue,
      changedBy: currentUser?.uid ?? 'admin',
      changedByName: currentUser?.displayName ?? currentUser?.email ?? 'Admin',
      comment:
        statusComment?.trim() ||
        `Status updated to ${PROPOSAL_STATUS_LABELS[statusValue] ?? statusValue}`,
      timestamp: new Date(),
    }

    try {
      await updateDoc(doc(db, 'proposals', proposal.id), {
        status: statusValue,
        statusHistory: arrayUnion({
          ...statusEvent,
          timestamp: statusEvent.timestamp,
        }),
        updatedAt: serverTimestamp(),
      })

      setProposal((prev) =>
        prev
          ? {
              ...prev,
              status: statusValue,
              statusHistory: [...prev.statusHistory, statusEvent],
            }
          : prev,
      )
      setStatusComment('')
      toast.success('Proposal status updated.')
    } catch (error) {
      toast.error('Failed to update status', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSavingStatus(false)
    }
  }

  const handleStatusUpdateClick = () => {
    if (statusValue === 'won' && proposal?.status !== 'won') {
      setConfirmOpen(true)
      return
    }
    handleStatusUpdate()
  }

  const handleSubmitReview = async () => {
    if (!proposal || savingReview) return
    const relevance = Number(reviewForm.relevance)
    const methodology = Number(reviewForm.methodology)
    const budget = Number(reviewForm.budget)
    const impact = Number(reviewForm.impact)
    const withinRange = [relevance, methodology, budget, impact].every(
      (value) => value >= 0 && value <= 25,
    )
    if (!withinRange) {
      toast.error('Scores must be between 0 and 25.')
      return
    }
    setSavingReview(true)
    const currentUser = auth.currentUser
    const review = {
      reviewerId: currentUser?.uid ?? 'admin',
      reviewerName: currentUser?.displayName ?? currentUser?.email ?? 'Admin',
      relevance,
      methodology,
      budget,
      impact,
      total: relevance + methodology + budget + impact,
      comments: reviewForm.comments?.trim() || '',
      conflictDeclared: reviewForm.conflictDeclared,
      date: new Date(),
    }

    try {
      await updateDoc(doc(db, 'proposals', proposal.id), {
        reviewScores: arrayUnion(review),
        updatedAt: serverTimestamp(),
      })

      setProposal((prev) =>
        prev
          ? {
              ...prev,
              reviewScores: [...prev.reviewScores, review],
            }
          : prev,
      )
      setReviewForm({
        relevance: 0,
        methodology: 0,
        budget: 0,
        impact: 0,
        comments: '',
        conflictDeclared: false,
      })
      toast.success('Review submitted.')
    } catch (error) {
      toast.error('Failed to submit review', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSavingReview(false)
    }
  }

  const budgetTotal = editableBudgetItems.reduce(
    (sum, item) => sum + Number(item.quantity || 0) * Number(item.unitCost || 0),
    0,
  )

  const updateBudgetItem = (index, field, value) => {
    setEditableBudgetItems((prev) =>
      prev.map((item, idx) => {
        if (idx !== index) return item
        const next = { ...item, [field]: value }
        const quantity = field === 'quantity' ? Number(value) : Number(next.quantity || 0)
        const unitCost = field === 'unitCost' ? Number(value) : Number(next.unitCost || 0)
        return {
          ...next,
          quantity,
          unitCost,
          total: quantity * unitCost,
        }
      }),
    )
  }

  const addBudgetLine = () => {
    setEditableBudgetItems((prev) => [
      ...prev,
      {
        id: `line-${Date.now()}`,
        category: 'Other',
        description: '',
        quantity: 1,
        unitCost: 0,
        total: 0,
      },
    ])
  }

  const removeBudgetLine = (index) => {
    setEditableBudgetItems((prev) => prev.filter((_, idx) => idx !== index))
  }

  const handleSaveBudget = async () => {
    if (!proposal || savingBudget) return
    setSavingBudget(true)
    const updatedItems = editableBudgetItems.map((item) => ({
      ...item,
      quantity: Number(item.quantity) || 0,
      unitCost: Number(item.unitCost) || 0,
      total: (Number(item.quantity) || 0) * (Number(item.unitCost) || 0),
    }))

    try {
      await updateDoc(doc(db, 'proposals', proposal.id), {
        budgetItems: updatedItems,
        totalBudget: budgetTotal,
        updatedAt: serverTimestamp(),
      })
      setProposal((prev) =>
        prev
          ? {
              ...prev,
              budgetItems: updatedItems,
              totalBudget: budgetTotal,
            }
          : prev,
      )
      toast.success('Budget updated successfully.')
    } catch (error) {
      toast.error('Failed to update budget', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setSavingBudget(false)
    }
  }

  const handleAddMember = async (event) => {
    event.preventDefault()
    if (!proposal) return
    if (!memberRole) {
      toast.error('Please select a member role.')
      return
    }

    setIsAddingMember(true)
    let memberPayload = null

    try {
      if (selectedMemberId === 'new') {
        if (!newMemberName.trim() || !newMemberEmail.trim()) {
          toast.error('Please enter name and email for the new member.')
          setIsAddingMember(false)
          return
        }
        const newUserRef = doc(collection(db, 'users'))
        const newUser = {
          fullName: newMemberName.trim(),
          email: newMemberEmail.trim(),
          userId: newUserRef.id,
          userType: memberRole,
          joinedAt: serverTimestamp(),
          isActive: true,
        }
        await setDoc(newUserRef, newUser)
        memberPayload = {
          userId: newUser.userId,
          fullName: newUser.fullName,
          email: newUser.email,
          role: memberRole,
          addedAt: new Date(),
        }
        setUsers((prev) => [...prev, { id: newUser.userId, ...newUser }])
      } else {
        const existingUser = users.find((user) => user.userId === selectedMemberId || user.id === selectedMemberId)
        if (!existingUser) {
          toast.error('Please select a valid member.')
          setIsAddingMember(false)
          return
        }
        memberPayload = {
          userId: existingUser.userId || existingUser.id,
          fullName: existingUser.fullName || existingUser.name || existingUser.email || 'User',
          email: existingUser.email || '',
          role: memberRole,
          addedAt: new Date(),
        }
      }

      await updateDoc(doc(db, 'proposals', proposal.id), {
        members: arrayUnion({
          ...memberPayload,
          addedAt: new Date(),
        }),
        updatedAt: serverTimestamp(),
      })

      setProposal((prev) =>
        prev
          ? {
              ...prev,
              members: [...prev.members, memberPayload],
            }
          : prev,
      )

      toast.success('Member added to proposal.')
      setSelectedMemberId('')
      setMemberRole('')
      setNewMemberName('')
      setNewMemberEmail('')
    } catch (error) {
      toast.error('Failed to add member', {
        description: error?.message || 'Please try again.',
      })
    } finally {
      setIsAddingMember(false)
    }
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">Loading proposal...</p>
      </div>
    )
  }

  if (!proposal) {
    return null
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" asChild className="size-8">
          <Link to="/dashboard/proposals">
            <ArrowLeft className="size-4" />
          </Link>
        </Button>
        <div className="flex flex-wrap items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground font-serif tracking-tight">
              {proposal.title}
            </h1>
            <p className="text-sm text-muted-foreground">
              {proposal.departmentId} | {(proposal.type || 'proposal').replace('_', ' ')} |{' '}
              {formatCurrency(proposal.totalBudget || 0, proposal.currency)}
            </p>
          </div>
          <StatusBadge status={proposal.status || 'draft'} />
        </div>
      </div>

      <Card>
        <CardContent className="p-5">
          <div className="mb-3 flex items-center gap-1">
            {stageKeys.map((stage, index) => (
              <div
                key={stage}
                className="h-2 flex-1 rounded-full"
                style={{
                  backgroundColor:
                    index <= currentIdx ? 'hsl(var(--primary))' : 'hsl(var(--muted))',
                }}
              />
            ))}
          </div>
          <div className="flex items-center justify-between gap-2 overflow-x-auto text-xs text-muted-foreground">
            {stageKeys.map((stage, index) => (
              <span
                key={stage}
                className={`whitespace-nowrap ${index === currentIdx ? 'font-semibold text-primary' : ''}`}
              >
                {PROPOSAL_STATUS_LABELS[stage]}
              </span>
            ))}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="timeline" className="space-y-4">
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="budget">Budget</TabsTrigger>
          <TabsTrigger value="reviews">
            Reviews {avgScore !== null ? `(${avgScore}/100)` : ''}
          </TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
        </TabsList>

        <TabsContent value="timeline">
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base">Proposal Summary</CardTitle>
              <CardDescription>All information captured during submission.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div>
                  <p className="text-xs text-muted-foreground">Title</p>
                  <p className="text-foreground">{proposal.title || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Type</p>
                  <p className="text-foreground capitalize">
                    {(proposal.type || 'proposal').replace('_', ' ')}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Department / School</p>
                  <p className="text-foreground">{proposal.departmentId || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Currency</p>
                  <p className="text-foreground">{proposal.currency || 'KES'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Budget</p>
                  <p className="text-foreground">
                    {formatCurrency(proposal.totalBudget || 0, proposal.currency)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <div className="mt-1">
                    <StatusBadge status={proposal.status || 'draft'} />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Submitted By</p>
                  <p className="text-foreground">{proposal.submittedByName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Created At</p>
                  <p className="text-foreground">{formatDate(proposal.createdAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Last Updated</p>
                  <p className="text-foreground">{formatDate(proposal.updatedAt)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Donor / Funding Agency</p>
                  <p className="text-foreground">{proposal.donorName || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Start Date</p>
                  <p className="text-foreground">{formatDate(proposal.startDate)}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">End Date</p>
                  <p className="text-foreground">{formatDate(proposal.endDate)}</p>
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs text-muted-foreground">Description</p>
                <p className="text-foreground">
                  {proposal.description || 'No description provided.'}
                </p>
              </div>

              <Separator />

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground">SDG Mappings</p>
                  {proposal.sdgMappings && proposal.sdgMappings.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {proposal.sdgMappings
                        .sort((a, b) => a - b)
                        .map((sdg) => (
                          <span
                            key={sdg}
                            className="rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground"
                          >
                            SDG {sdg}
                          </span>
                        ))}
                    </div>
                  ) : (
                    <p className="text-foreground">—</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Strategic Pillars</p>
                  {proposal.strategicPillars && proposal.strategicPillars.length > 0 ? (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {proposal.strategicPillars.map((pillar) => (
                        <span
                          key={pillar}
                          className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold text-muted-foreground"
                        >
                          {pillar}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-foreground">—</p>
                  )}
                </div>
              </div>

              <Separator />

              <div>
                <p className="text-xs text-muted-foreground">Conflict of Interest Declaration</p>
                <p className="text-foreground">
                  {proposal.conflictDeclaration ? 'Declared' : 'Not declared'}
                </p>
              </div>

              <Separator />

              <div>
                <p className="text-xs text-muted-foreground">Documents</p>
                {proposal.documentUrls && proposal.documentUrls.length > 0 ? (
                  <ul className="mt-1 space-y-1 text-sm text-foreground">
                    {proposal.documentUrls.map((docItem, index) => {
                      const docUrl = typeof docItem === 'string' ? docItem : docItem?.url
                      const docName =
                        typeof docItem === 'string' ? `Document ${index + 1}` : docItem?.name
                      return (
                        <li key={`${docUrl || docItem?.path || index}`}>
                          {docUrl ? (
                            <a
                              className="font-medium text-primary hover:underline"
                              href={docUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {docName || `Document ${index + 1}`}
                            </a>
                          ) : (
                            <span>{docName || `Document ${index + 1}`}</span>
                          )}
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <p className="text-foreground">—</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base">Update Status</CardTitle>
              <CardDescription>Move this proposal through the review workflow</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>New Status</Label>
                  <Select value={statusValue} onValueChange={setStatusValue} disabled={!canEdit}>
                    <SelectTrigger disabled={!canEdit}>
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(PROPOSAL_STATUS_LABELS).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Comment</Label>
                  <Textarea
                    value={statusComment}
                    onChange={(event) => setStatusComment(event.target.value)}
                    placeholder="Optional status note..."
                    rows={3}
                    disabled={!canEdit}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleStatusUpdateClick} disabled={savingStatus || !canEdit}>
                  {savingStatus ? 'Updating...' : 'Update Status'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status History</CardTitle>
              <CardDescription>Track the progress of this proposal through the review workflow</CardDescription>
            </CardHeader>
            <CardContent>
              {proposal.statusHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No status updates yet.
                </p>
              ) : (
                <div className="relative ml-4 space-y-6 border-l-2 border-muted pl-6">
                  {proposal.statusHistory.map((event, index) => (
                    <div key={`${event.status}-${index}`} className="relative">
                      <div className="absolute -left-[31px] flex size-5 items-center justify-center rounded-full border-2 border-primary bg-card">
                        {index === proposal.statusHistory.length - 1 ? (
                          <Clock className="size-3 text-primary" />
                        ) : event.status === 'won' ? (
                          <CheckCircle2 className="size-3 text-emerald-500" />
                        ) : event.status === 'lost' ? (
                          <XCircle className="size-3 text-red-500" />
                        ) : (
                          <div className="size-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusBadge status={event.status} />
                          <span className="text-xs text-muted-foreground">
                            {formatDate(normalizeDate(event.timestamp))}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-foreground">{event.comment}</p>
                        <p className="text-xs text-muted-foreground">by {event.changedByName}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="budget">
          <Card>
            <CardHeader>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <CardTitle className="text-base">Budget Breakdown</CardTitle>
                  <CardDescription>
                    {isEditable
                      ? 'Update the proposed budget by category'
                      : 'Budget edits are locked after the draft stage.'}
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={addBudgetLine} disabled={!isEditable}>
                    Add Line
                  </Button>
                  <Button onClick={handleSaveBudget} disabled={savingBudget || !isEditable}>
                    {savingBudget ? 'Saving...' : 'Save Budget'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Category</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Qty</TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-[60px]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {editableBudgetItems.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-muted-foreground">
                        No budget lines yet.
                      </TableCell>
                    </TableRow>
                  ) : (
                    editableBudgetItems.map((item, index) => (
                      <TableRow key={item.id || index}>
                        <TableCell>
                          <Input
                            value={item.category}
                            disabled={!isEditable}
                            onChange={(event) => updateBudgetItem(index, 'category', event.target.value)}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.description}
                            disabled={!isEditable}
                            onChange={(event) => updateBudgetItem(index, 'description', event.target.value)}
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min={0}
                            value={item.quantity}
                            disabled={!isEditable}
                            onChange={(event) =>
                              updateBudgetItem(index, 'quantity', Number(event.target.value) || 0)
                            }
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <Input
                            type="number"
                            min={0}
                            value={item.unitCost}
                            disabled={!isEditable}
                            onChange={(event) =>
                              updateBudgetItem(index, 'unitCost', Number(event.target.value) || 0)
                            }
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.total, proposal.currency)}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            onClick={() => removeBudgetLine(index)}
                            disabled={!isEditable}
                          >
                            <XCircle className="size-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                  <TableRow className="font-bold">
                    <TableCell colSpan={4}>Grand Total</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(budgetTotal, proposal.currency)}
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reviews">
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base">Submit Review</CardTitle>
              <CardDescription>Score the proposal and provide reviewer comments</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Relevance (0-25)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={25}
                    value={reviewForm.relevance}
                    onChange={(event) =>
                      setReviewForm((prev) => ({ ...prev, relevance: Number(event.target.value) }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Methodology (0-25)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={25}
                    value={reviewForm.methodology}
                    onChange={(event) =>
                      setReviewForm((prev) => ({ ...prev, methodology: Number(event.target.value) }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Budget (0-25)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={25}
                    value={reviewForm.budget}
                    onChange={(event) =>
                      setReviewForm((prev) => ({ ...prev, budget: Number(event.target.value) }))
                    }
                  />
                </div>
                <div className="space-y-2">
                  <Label>Impact (0-25)</Label>
                  <Input
                    type="number"
                    min={0}
                    max={25}
                    value={reviewForm.impact}
                    onChange={(event) =>
                      setReviewForm((prev) => ({ ...prev, impact: Number(event.target.value) }))
                    }
                  />
                </div>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Total Score</span>
                <span className="font-semibold text-foreground">{reviewTotal}/100</span>
              </div>
              <div className="space-y-2">
                <Label>Reviewer Comments</Label>
                <Textarea
                  value={reviewForm.comments}
                  onChange={(event) =>
                    setReviewForm((prev) => ({ ...prev, comments: event.target.value }))
                  }
                  placeholder="Add strengths, gaps, and recommendations..."
                  rows={4}
                />
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="conflict"
                  checked={reviewForm.conflictDeclared}
                  onCheckedChange={(value) =>
                    setReviewForm((prev) => ({ ...prev, conflictDeclared: value === true }))
                  }
                />
                <Label htmlFor="conflict" className="cursor-pointer text-sm">
                  I declare no conflict of interest for this review.
                </Label>
              </div>
              <div className="flex justify-end">
                <Button onClick={handleSubmitReview} disabled={savingReview}>
                  {savingReview ? 'Submitting...' : 'Submit Review'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Reviewer Scores</CardTitle>
              <CardDescription>Scoring matrix from assigned reviewers</CardDescription>
            </CardHeader>
            <CardContent>
              {proposal.reviewScores.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No reviews submitted yet
                </p>
              ) : (
                <div className="space-y-6">
                  {proposal.reviewScores.map((review) => (
                    <div key={review.reviewerId} className="rounded-lg border p-4">
                      <div className="mb-4 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-foreground">{review.reviewerName}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(normalizeDate(review.date))}
                          </p>
                        </div>
                        <Badge className="border-0 bg-primary text-primary-foreground">
                          {review.total}/100
                        </Badge>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        {[
                          { label: 'Relevance', value: review.relevance, max: 25 },
                          { label: 'Methodology', value: review.methodology, max: 25 },
                          { label: 'Budget', value: review.budget, max: 25 },
                          { label: 'Impact', value: review.impact, max: 25 },
                        ].map((criterion) => (
                          <div key={criterion.label}>
                            <div className="mb-1 flex justify-between text-xs">
                              <span className="text-muted-foreground">{criterion.label}</span>
                              <span className="font-medium text-foreground">
                                {criterion.value}/{criterion.max}
                              </span>
                            </div>
                            <Progress value={(criterion.value / criterion.max) * 100} className="h-2" />
                          </div>
                        ))}
                      </div>
                      {review.comments ? (
                        <p className="mt-3 text-sm text-muted-foreground italic">
                          &ldquo;{review.comments}&rdquo;
                        </p>
                      ) : null}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members">
          <Card className="mb-4">
            <CardHeader>
              <CardTitle className="text-base">Add Proposal Member</CardTitle>
              <CardDescription>
                {isEditable
                  ? 'Assign collaborators to this proposal. If the user does not exist, create a new entry.'
                  : 'Member edits are locked after the draft stage.'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={handleAddMember}>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label>Member</Label>
                    <Select
                      value={selectedMemberId}
                      onValueChange={setSelectedMemberId}
                      disabled={!isEditable}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="new">+ Add new user</SelectItem>
                        {users.map((user) => (
                          <SelectItem key={user.userId || user.id} value={user.userId || user.id}>
                            {user.fullName || user.name || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select value={memberRole} onValueChange={setMemberRole} disabled={!isEditable}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableRoles.length === 0 ? (
                          <SelectItem value="no-roles" disabled>
                            No roles available
                          </SelectItem>
                        ) : (
                          availableRoles.map((role) => (
                            <SelectItem key={role} value={role}>
                              {role}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {selectedMemberId === 'new' ? (
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        value={newMemberName}
                        disabled={!isEditable}
                        onChange={(event) => setNewMemberName(event.target.value)}
                        placeholder="e.g., Dr. Jane Odhiambo"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={newMemberEmail}
                        disabled={!isEditable}
                        onChange={(event) => setNewMemberEmail(event.target.value)}
                        placeholder="e.g., j.odhiambo@jooust.ac.ke"
                        required
                      />
                    </div>
                  </div>
                ) : null}

                <div className="flex justify-end">
                  <Button type="submit" disabled={isAddingMember || !isEditable}>
                    {isAddingMember ? 'Adding...' : 'Add Member'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Current Members</CardTitle>
              <CardDescription>People currently assigned to this proposal.</CardDescription>
            </CardHeader>
            <CardContent>
              {proposal.members.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">
                  No members assigned yet.
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Added</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proposal.members.map((member, index) => (
                      <TableRow key={`${member.userId}-${index}`}>
                        <TableCell className="font-medium">{member.fullName}</TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>{member.role}</TableCell>
                        <TableCell>{formatDate(member.addedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Winning Status</DialogTitle>
            <DialogDescription>
              Marking this proposal as won is a significant change. You can continue if you are sure.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setConfirmOpen(false)
              }}
              disabled={savingStatus}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setConfirmOpen(false)
                handleStatusUpdate()
              }}
              disabled={savingStatus || !canEdit}
            >
              Update Status
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
